import { useCallback, useEffect, useMemo, useState } from "react";
import usePromiseLoadFetcher from "../../../../common/core-ui/usePromiseLoadFetcher.ts";
import useSearchFavoritesSync from "./useSearchFavoritesSync.ts";
import {
  CompositeAsset,
  Favorite,
} from "../../../../common/api/decimal/common-types/manageAssets.ts";
import { SearchStatus } from "../types.ts";

interface PaginatedSearchResponse<T> {
  totalItems: number;
  totalPages: number;
  page: number;
  results: T[];
}

interface Options {
  query?: string;
  route: string;
  enabled: boolean;
  chunkSize: number;
}

interface ResultsInfo<T> {
  items: T[];
  sizes: number[];
  isReady: boolean;
  isLoadingMore: boolean;
  totalPages: number;
  totalItems: number;
}

export default function usePaginatedTitleResults<
  T extends CompositeAsset & { favorite: Favorite },
  R extends PaginatedSearchResponse<T>,
>({ query, route, enabled, chunkSize }: Options) {
  // Be careful about reading off the fetcher! It can lead to slight timing issues
  // due to not being synced with itemInfo
  const promiseFetcher = usePromiseLoadFetcher<R>();
  const [pageIndex, setPageIndex] = useState(0);
  const [resultsInfo, setResultsInfo] = useState<ResultsInfo<T>>({
    items: [],
    sizes: [],
    isReady: false,
    isLoadingMore: false,
    totalPages: 0,
    totalItems: 0,
  });

  useSearchFavoritesSync<T>(resultsInfo?.items, (updatedItems) => {
    setResultsInfo((oldInfo) => ({ ...oldInfo, items: updatedItems }));
  });

  const status = useMemo<SearchStatus>(() => {
    if (!enabled) {
      return "empty";
    } else if (resultsInfo.isReady) {
      return resultsInfo.items.length ? "has-results" : "empty";
    }
    return "loading";
  }, [resultsInfo, enabled]);

  const onLastPage =
    resultsInfo.isReady &&
    resultsInfo.totalPages > 0 &&
    resultsInfo.sizes.length >= resultsInfo.totalPages;

  const hasMore = status === "has-results" && !onLastPage;

  const fetchNextPage = useCallback(() => {
    setPageIndex((currentPage) => currentPage + 1);
  }, []);

  const loadMoreRequest = useCallback(() => {
    if (hasMore) {
      setResultsInfo((info) => ({ ...info, isLoadingMore: true }));
      fetchNextPage();
    } else {
      // Simulate a load to help with render syncing of combined results
      setResultsInfo((info) => ({ ...info, isLoadingMore: true }));
      window.setTimeout(() => {
        setResultsInfo((info) => ({
          ...info,
          sizes: [...info.sizes, 0],
          isLoadingMore: false,
        }));
      }, 50);
    }
  }, [hasMore, fetchNextPage]);

  useEffect(() => {
    if (
      query &&
      enabled &&
      promiseFetcher.state === "idle" &&
      (!promiseFetcher.data || promiseFetcher.data.page < pageIndex)
    ) {
      const queryParams = new URLSearchParams();
      queryParams.set("q", query);
      queryParams.set("page", pageIndex.toString());
      queryParams.set("size", chunkSize.toString());

      promiseFetcher
        .load(`${route}?${queryParams.toString()}`)
        .then((fetcher) => {
          // sometimes we are given the same vbid on different pages so remove duplicates
          const uniqueItems = fetcher.data!.results.filter(
            (newItem) =>
              !resultsInfo.items.some(
                (existingItem) =>
                  existingItem.manageAsset.vbid === newItem.manageAsset.vbid
              )
          );

          if (uniqueItems.length === 0) {
            // page is the page index
            if (fetcher.data!.page >= fetcher.data!.totalPages - 1) {
              // We're at the end, so we're done
              setResultsInfo((info) => ({
                ...info,
                isReady: true,
                isLoadingMore: false,
              }));
            } else {
              fetchNextPage();
            }
          } else {
            setResultsInfo((currentItemInfo) => {
              return {
                isReady: true,
                isLoadingMore: false,
                totalPages: fetcher.data!.totalPages,
                totalItems: fetcher.data!.totalItems,
                sizes: [...currentItemInfo.sizes, uniqueItems.length],
                items: [...currentItemInfo.items, ...uniqueItems],
              };
            });
          }
        })
        .catch(() => {
          // nothing to do
        });
    }
  }, [
    enabled,
    promiseFetcher,
    query,
    route,
    pageIndex,
    resultsInfo,
    fetchNextPage,
    chunkSize,
  ]);

  return useMemo(
    () => ({
      status,
      totalItems: resultsInfo.totalItems,
      items: resultsInfo.items,
      sizes: resultsInfo.sizes,
      hasMore,
      loadMore: loadMoreRequest,
      isLoadingMore: resultsInfo.isLoadingMore,
    }),
    [status, resultsInfo, hasMore, loadMoreRequest]
  );
}
